import React, { useState, useEffect } from 'react';
import { useAuth } from '../../../../context/AuthContext';
import axios from 'axios';
import {
  Tags,
  Plus,
  Search,
  MoreVertical,
  Edit,
  Trash2,
  RefreshCw,
  Lock,
  Globe,
  Filter,
  X
} from 'lucide-react';
import { Button } from '../../../../components/ui/button';
import { Input } from '../../../../components/ui/input';
import { Badge } from '../../../../components/ui/badge';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../../../../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../../../components/ui/select';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../../../../components/ui/dropdown-menu';
import { Switch } from '../../../../components/ui/switch';
import { cn } from '../../../../lib/utils';
import { toast } from 'sonner';

// Normalize backend URL to prevent double /api paths
const rawBackendUrl = (process.env.REACT_APP_BACKEND_URL || '').trim().replace(/\/$/, '');
const BACKEND_URL = rawBackendUrl.replace(/\/api$/, '');
const API = `${BACKEND_URL}/api`;

const TAG_COLORS = [
  { value: '#10b981', label: 'Emerald' },
  { value: '#f59e0b', label: 'Amber' },
  { value: '#ef4444', label: 'Red' },
  { value: '#3b82f6', label: 'Blue' },
  { value: '#8b5cf6', label: 'Purple' },
  { value: '#ec4899', label: 'Pink' },
  { value: '#14b8a6', label: 'Teal' },
  { value: '#f97316', label: 'Orange' },
  { value: '#6b7280', label: 'Gray' },
];

const PlatformTagsPage = () => {
  const { token } = useAuth();
  const [tags, setTags] = useState([]);
  const [objects, setObjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedObject, setSelectedObject] = useState(null);
  const [showAddModal, setShowAddModal] = useState(false);
  const [editingTag, setEditingTag] = useState(null);
  
  // Form state
  const [formData, setFormData] = useState({
    name: '',
    color: '#10b981',
    object_type: '',
    available_to_agencies: false,
    is_locked: false
  });

  useEffect(() => {
    fetchData();
  }, [token]);

  const fetchData = async () => {
    if (!token) return;
    setLoading(true);
    try {
      const [tagsRes, objectsRes] = await Promise.all([
        axios.get(`${API}/platform/settings/data/tags`, {
          headers: { Authorization: `Bearer ${token}` }
        }),
        axios.get(`${API}/platform/settings/data/objects`, {
          headers: { Authorization: `Bearer ${token}` }
        })
      ]);
      setTags(tagsRes.data.tags || []);
      setObjects(objectsRes.data.objects || []);
    } catch (err) {
      console.error('Failed to fetch data:', err);
      toast.error('Failed to load tags');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async () => {
    if (!formData.name || !formData.object_type) {
      toast.error('Name and object type are required');
      return;
    }

    try {
      if (editingTag) {
        await axios.put(`${API}/platform/settings/data/tags/${editingTag.id}`, formData, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success('Tag updated');
      } else {
        await axios.post(`${API}/platform/settings/data/tags`, formData, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success('Tag created');
      }
      setShowAddModal(false);
      setEditingTag(null);
      resetForm();
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to save tag');
    }
  };

  const handleDelete = async (tagId) => {
    if (!window.confirm('Are you sure you want to delete this tag?')) return;
    
    try {
      await axios.delete(`${API}/platform/settings/data/tags/${tagId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Tag deleted');
      fetchData();
    } catch (err) {
      toast.error('Failed to delete tag');
    }
  };

  const resetForm = () => {
    setFormData({
      name: '',
      color: '#10b981',
      object_type: '',
      available_to_agencies: false,
      is_locked: false
    });
  };

  const openEditModal = (tag) => {
    setEditingTag(tag);
    setFormData({
      name: tag.name,
      color: tag.color,
      object_type: tag.object_type,
      available_to_agencies: tag.available_to_agencies,
      is_locked: tag.is_locked
    });
    setShowAddModal(true);
  };

  const filteredTags = tags.filter(tag => {
    const matchesSearch = tag.name.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesObject = !selectedObject || tag.object_type === selectedObject;
    return matchesSearch && matchesObject;
  });

  // Group by object type
  const groupedByObject = {};
  filteredTags.forEach(tag => {
    if (!groupedByObject[tag.object_type]) {
      groupedByObject[tag.object_type] = [];
    }
    groupedByObject[tag.object_type].push(tag);
  });

  return (
    <div className="min-h-screen bg-[#0B1F18] p-8" data-testid="platform-tags-page">
      {/* Header */}
      <div className="flex items-center justify-between mb-8">
        <div className="flex items-center gap-4">
          <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-purple-500 to-purple-600 flex items-center justify-center">
            <Tags className="w-6 h-6 text-white" />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-white">Platform Tags</h1>
            <p className="text-[#93AE9F] text-sm">
              Create global tags for agencies, users, tickets, and other objects
            </p>
          </div>
        </div>
        <Button
          onClick={() => {
            resetForm();
            setEditingTag(null);
            setShowAddModal(true);
          }}
          className="bg-amber-500 hover:bg-amber-600 text-white"
          data-testid="add-tag-btn"
        >
          <Plus className="w-4 h-4 mr-2" />
          Add Tag
        </Button>
      </div>

      {/* Search and Filter */}
      <div className="flex items-center gap-4 mb-6">
        <div className="relative flex-1 max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-500" size={18} />
          <Input
            placeholder="Search tags..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="pl-10 bg-zinc-900/50 border-zinc-700 text-white"
          />
        </div>
        <Select
          value={selectedObject || 'all'}
          onValueChange={(v) => setSelectedObject(v === 'all' ? null : v)}
        >
          <SelectTrigger className="w-48 bg-zinc-900/50 border-zinc-700 text-white">
            <Filter className="w-4 h-4 mr-2 text-zinc-500" />
            <SelectValue placeholder="Filter by object" />
          </SelectTrigger>
          <SelectContent className="bg-zinc-800 border-zinc-700">
            <SelectItem value="all">All Objects</SelectItem>
            {objects.filter(o => o.is_active).map((obj) => (
              <SelectItem key={obj.id} value={obj.name}>{obj.name}</SelectItem>
            ))}
          </SelectContent>
        </Select>
        {selectedObject && (
          <Button
            variant="outline"
            size="sm"
            onClick={() => setSelectedObject(null)}
            className="border-zinc-600 text-zinc-400"
          >
            <X className="w-4 h-4 mr-1" />
            Clear
          </Button>
        )}
      </div>

      {/* Tags Grid */}
      {loading ? (
        <div className="flex items-center justify-center py-12">
          <RefreshCw className="w-6 h-6 text-emerald-500 animate-spin" />
        </div>
      ) : filteredTags.length === 0 ? (
        <div className="bg-[rgba(10,38,28,0.6)] border border-[rgba(142,218,182,0.12)] rounded-xl p-12 text-center">
          <Tags className="w-12 h-12 text-zinc-600 mx-auto mb-4" />
          <h3 className="text-lg font-semibold text-white mb-2">No Tags Found</h3>
          <p className="text-[#93AE9F] text-sm mb-4">
            Create tags to categorize and organize platform data.
          </p>
          <Button
            onClick={() => {
              resetForm();
              setShowAddModal(true);
            }}
            className="bg-amber-500 hover:bg-amber-600 text-white"
          >
            <Plus className="w-4 h-4 mr-2" />
            Create First Tag
          </Button>
        </div>
      ) : (
        <div className="space-y-6">
          {Object.entries(groupedByObject).map(([objectType, objectTags]) => (
            <div key={objectType} className="bg-[rgba(10,38,28,0.6)] border border-[rgba(142,218,182,0.12)] rounded-xl p-6">
              <h3 className="text-sm font-semibold text-[#93AE9F] uppercase tracking-wider mb-4 flex items-center gap-2">
                {objectType}
                <Badge className="bg-zinc-700 text-zinc-300 border-0 text-xs">{objectTags.length}</Badge>
              </h3>
              <div className="flex flex-wrap gap-3">
                {objectTags.map((tag) => (
                  <div
                    key={tag.id}
                    className="group flex items-center gap-2 px-3 py-2 rounded-lg bg-zinc-900/50 border border-zinc-700 hover:border-zinc-600 transition-colors"
                  >
                    <div
                      className="w-3 h-3 rounded-full"
                      style={{ backgroundColor: tag.color }}
                    />
                    <span className="text-white font-medium">{tag.name}</span>
                    {tag.is_locked && (
                      <Lock className="w-3 h-3 text-zinc-500" />
                    )}
                    {tag.available_to_agencies && (
                      <Globe className="w-3 h-3 text-blue-400" title="Available to agencies" />
                    )}
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <button className="ml-1 p-1 rounded opacity-0 group-hover:opacity-100 hover:bg-zinc-700 transition-all">
                          <MoreVertical className="w-3 h-3 text-zinc-400" />
                        </button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent className="bg-zinc-900 border-zinc-700">
                        <DropdownMenuItem
                          onClick={() => openEditModal(tag)}
                          className="text-zinc-300 hover:text-white focus:text-white"
                        >
                          <Edit className="w-4 h-4 mr-2" />
                          Edit
                        </DropdownMenuItem>
                        <DropdownMenuItem
                          onClick={() => handleDelete(tag.id)}
                          className="text-red-400 hover:text-red-300 focus:text-red-300"
                        >
                          <Trash2 className="w-4 h-4 mr-2" />
                          Delete
                        </DropdownMenuItem>
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      )}
      
      <div className="mt-4 text-sm text-[#93AE9F]">
        {filteredTags.length} tags total
      </div>

      {/* Add/Edit Tag Modal */}
      <Dialog open={showAddModal} onOpenChange={setShowAddModal}>
        <DialogContent className="bg-zinc-900 border-zinc-700 text-white max-w-md">
          <DialogHeader>
            <DialogTitle>{editingTag ? 'Edit Tag' : 'Add Tag'}</DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div>
              <label className="block text-sm font-medium text-zinc-400 mb-2">Tag Name *</label>
              <Input
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                placeholder="e.g., high-priority"
                className="bg-zinc-800 border-zinc-700 text-white"
              />
            </div>
            
            <div>
              <label className="block text-sm font-medium text-zinc-400 mb-2">Object Type *</label>
              <Select
                value={formData.object_type}
                onValueChange={(v) => setFormData({ ...formData, object_type: v })}
              >
                <SelectTrigger className="bg-zinc-800 border-zinc-700 text-white">
                  <SelectValue placeholder="Select object type" />
                </SelectTrigger>
                <SelectContent className="bg-zinc-800 border-zinc-700">
                  {objects.filter(o => o.is_active).map((obj) => (
                    <SelectItem key={obj.id} value={obj.name}>{obj.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            
            <div>
              <label className="block text-sm font-medium text-zinc-400 mb-2">Color</label>
              <div className="flex flex-wrap gap-2">
                {TAG_COLORS.map((color) => (
                  <button
                    key={color.value}
                    onClick={() => setFormData({ ...formData, color: color.value })}
                    className={cn(
                      'w-8 h-8 rounded-lg transition-all',
                      formData.color === color.value
                        ? 'ring-2 ring-white ring-offset-2 ring-offset-zinc-900'
                        : 'hover:scale-110'
                    )}
                    style={{ backgroundColor: color.value }}
                    title={color.label}
                  />
                ))}
              </div>
            </div>
            
            <div className="space-y-3 pt-2">
              <div className="flex items-center justify-between">
                <div>
                  <label className="text-sm font-medium text-white">Locked</label>
                  <p className="text-xs text-zinc-500">Agencies cannot modify or delete this tag</p>
                </div>
                <Switch
                  checked={formData.is_locked}
                  onCheckedChange={(checked) => setFormData({ ...formData, is_locked: checked })}
                />
              </div>
              
              <div className="flex items-center justify-between">
                <div>
                  <label className="text-sm font-medium text-white">Available to Agencies</label>
                  <p className="text-xs text-zinc-500">Agencies can use this tag</p>
                </div>
                <Switch
                  checked={formData.available_to_agencies}
                  onCheckedChange={(checked) => setFormData({ ...formData, available_to_agencies: checked })}
                />
              </div>
            </div>
          </div>
          
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => {
                setShowAddModal(false);
                setEditingTag(null);
                resetForm();
              }}
              className="border-zinc-600 text-zinc-300"
            >
              Cancel
            </Button>
            <Button
              onClick={handleSubmit}
              className="bg-amber-500 hover:bg-amber-600 text-white"
            >
              {editingTag ? 'Save Changes' : 'Create Tag'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default PlatformTagsPage;
